import { euroToCents } from './euroAmount'
import { parseThreshold } from './productThreshold'
/**
 * Utility di validazione centralizzata per form React.
 * 
 * Contiene pattern regex e funzioni di validazione per tutti i form del frontend.
 * Garantisce feedback immediato all'utente e previene richieste non valide al backend.
 */

// ============================================
// Pattern Regex
// ============================================

export const PATTERNS = {
  // Email standard
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  
  // CAP italiano (5 cifre)
  CAP_IT: /^\d{5}$/,
  
  // Provincia italiana (2 lettere)
  PROVINCIA_IT: /^[A-Za-z]{2}$/,
  
  // Codice fiscale italiano (16 caratteri alfanumerici)
  CODICE_FISCALE: /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i,
  
  // Partita IVA italiana (11 cifre)
  PARTITA_IVA: /^\d{11}$/,
  
  // Codice SDI (7 caratteri alfanumerici)
  CODICE_SDI: /^[A-Z0-9]{7}$/i,
  
  // Telefono generico (accetta vari formati)
  TELEFONO: /^[\d\s\+\-\.]{6,20}$/,
  
  // Codice alfanumerico (per codici prodotto, box, etc)
  CODICE_ALFANUMERICO: /^[A-Za-z0-9\-_]+$/,
  
  // Solo numeri
  SOLO_NUMERI: /^\d+$/,
  
  // Numero decimale
  NUMERO_DECIMALE: /^\d+([.,]\d{1,2})?$/,
}

// ============================================
// Messaggi di Errore
// ============================================

export const MESSAGES = {
  REQUIRED: 'Campo obbligatorio',
  MIN_LENGTH: (min) => `Minimo ${min} caratteri`,
  MAX_LENGTH: (max) => `Massimo ${max} caratteri`,
  EMAIL_INVALID: 'Formato email non valido',
  CAP_INVALID: 'Il CAP deve essere di 5 cifre',
  PROVINCIA_INVALID: 'La provincia deve essere di 2 lettere (es: NA, RM)',
  CF_INVALID: 'Codice fiscale non valido (16 caratteri)',
  PIVA_INVALID: 'Partita IVA non valida (11 cifre)',
  SDI_INVALID: 'Codice SDI non valido (7 caratteri)',
  TELEFONO_INVALID: 'Formato telefono non valido',
  CODICE_INVALID: 'Codice non valido (solo lettere, numeri, - e _)',
  NUMERO_POSITIVO: 'Il valore deve essere maggiore di 0',
  NUMERO_NON_NEGATIVO: 'Il valore non può essere negativo',
  PREZZO_VENDITA_MAGGIORE: 'Il prezzo di vendita deve essere maggiore del prezzo di acquisto',
}

// ============================================
// Funzioni di Validazione
// ============================================

/**
 * Valida che un campo sia obbligatorio
 */
export const required = (value, message = MESSAGES.REQUIRED) => {
  if (value === null || value === undefined || value === '') {
    return message
  }
  if (typeof value === 'string' && value.trim() === '') {
    return message
  }
  return null
}

/**
 * Valida lunghezza minima
 */
export const minLength = (value, min, message = MESSAGES.MIN_LENGTH(min)) => {
  if (!value) return null // Non valida se vuoto (usa required per quello)
  if (value.length < min) {
    return message
  }
  return null
}

/**
 * Valida lunghezza massima
 */
export const maxLength = (value, max, message = MESSAGES.MAX_LENGTH(max)) => {
  if (!value) return null
  if (value.length > max) {
    return message
  }
  return null
}

/**
 * Valida contro un pattern regex
 */
export const pattern = (value, regex, message) => {
  if (!value) return null
  if (!regex.test(value)) {
    return message
  }
  return null
}

/**
 * Valida formato email
 */
export const email = (value, message = MESSAGES.EMAIL_INVALID) => {
  return pattern(value, PATTERNS.EMAIL, message)
}

/**
 * Valida CAP italiano (5 cifre)
 */
export const capIT = (value, message = MESSAGES.CAP_INVALID) => {
  if (!value) return null
  // Pulisci il valore rimuovendo spazi
  const cleanValue = value.replace(/\s/g, '')
  return pattern(cleanValue, PATTERNS.CAP_IT, message)
}

/**
 * Valida provincia italiana (2 lettere)
 */
export const provinciaIT = (value, message = MESSAGES.PROVINCIA_INVALID) => {
  return pattern(value, PATTERNS.PROVINCIA_IT, message)
}

/**
 * Valida codice fiscale italiano
 */
export const codiceFiscale = (value, message = MESSAGES.CF_INVALID) => {
  return pattern(value, PATTERNS.CODICE_FISCALE, message)
}

/**
 * Valida partita IVA italiana
 */
export const partitaIva = (value, message = MESSAGES.PIVA_INVALID) => {
  return pattern(value, PATTERNS.PARTITA_IVA, message)
}

/**
 * Valida codice SDI
 */
export const codiceSdi = (value, message = MESSAGES.SDI_INVALID) => {
  return pattern(value, PATTERNS.CODICE_SDI, message)
}

/**
 * Valida telefono generico
 */
export const telefono = (value, message = MESSAGES.TELEFONO_INVALID) => {
  return pattern(value, PATTERNS.TELEFONO, message)
}

/**
 * Valida codice alfanumerico
 */
export const codiceAlfanumerico = (value, message = MESSAGES.CODICE_INVALID) => {
  return pattern(value, PATTERNS.CODICE_ALFANUMERICO, message)
}

/**
 * Valida numero positivo (> 0)
 */
export const numeroPositivo = (value, message = MESSAGES.NUMERO_POSITIVO) => {
  if (!value && value !== 0) return null
  const num = parseFloat(value)
  if (isNaN(num) || num <= 0) {
    return message
  }
  return null
}

/**
 * Valida numero non negativo (>= 0)
 */
export const numeroNonNegativo = (value, message = MESSAGES.NUMERO_NON_NEGATIVO) => {
  if (!value && value !== 0) return null
  const num = parseFloat(value)
  if (isNaN(num) || num < 0) {
    return message
  }
  return null
}

// ============================================
// Validatori Composti per Form Specifici
// ============================================

/**
 * Valida form cliente
 */
export const validateReferenteAzienda = (referente = {}, mandatory = true) => {
  const fields = ['nome', 'cognome', 'telefono']
  const hasData = [...fields, 'email'].some((field) => referente[field]?.trim())
  if (!mandatory && !hasData) return {}

  const errors = {}
  for (const field of fields) {
    if (!referente[field]?.trim()) errors[field] = 'Campo obbligatorio'
  }
  if (referente.telefono && !errors.telefono) {
    errors.telefono = telefono(referente.telefono)
  }
  if (referente.email) errors.email = email(referente.email)
  return Object.fromEntries(Object.entries(errors).filter(([, value]) => value))
}

export const validateCliente = (formData, { requireReferente = true } = {}) => {
  const errors = {}
  
  if (formData.tipo === 'privato') {
    // Privato: nome e cognome obbligatori
    errors.nome = required(formData.nome) || minLength(formData.nome, 2)
    errors.cognome = required(formData.cognome) || minLength(formData.cognome, 2)
    
    // Codice fiscale opzionale ma se presente deve essere valido
    if (formData.codice_fiscale) {
      errors.codice_fiscale = codiceFiscale(formData.codice_fiscale)
    }
  } else {
    // Azienda: ragione sociale e partita IVA obbligatorie
    errors.ragione_sociale = required(formData.ragione_sociale) || minLength(formData.ragione_sociale, 2)
    errors.partita_iva = required(formData.partita_iva) || partitaIva(formData.partita_iva)
    
    // Codice SDI opzionale ma se presente deve essere valido
    if (formData.codice_sdi) {
      errors.codice_sdi = codiceSdi(formData.codice_sdi)
    }
    const referenteErrors = validateReferenteAzienda(formData.referente_azienda, requireReferente)
    if (Object.keys(referenteErrors).length) errors.referente_azienda = referenteErrors
  }
  
  // Email facoltativa per tutti; telefono obbligatorio per i privati.
  if (formData.email) errors.email = email(formData.email)
  if (formData.tipo === 'privato') {
    errors.telefono = required(formData.telefono) || telefono(formData.telefono)
  } else {
    if (formData.telefono) errors.telefono = telefono(formData.telefono)
  }

  // Venditore obbligatorio (Punto 3 feedback cliente)
  errors.venditore = required(formData.venditore, 'Seleziona il venditore che ha acquisito il contatto')
  
  // PEC opzionale ma se presente deve essere valida
  if (formData.pec) {
    errors.pec = email(formData.pec)
  }
  
  if (formData.cellulare) {
    errors.cellulare = telefono(formData.cellulare)
  }
  
  // Rimuovi errori null
  return Object.fromEntries(
    Object.entries(errors).filter(([_, v]) => v !== null)
  )
}

/**
 * Valida form indirizzo
 */
export const validateIndirizzo = (formData) => {
  const errors = {}
  
  errors.via = required(formData.via) || minLength(formData.via, 3)
  errors.cap = required(formData.cap)
  errors.citta = required(formData.citta) || minLength(formData.citta, 2)
  errors.provincia = required(formData.provincia)
  
  // Validazioni specifiche per Italia
  if (formData.nazione === 'IT') {
    if (formData.cap) {
      errors.cap = errors.cap || capIT(formData.cap)
    }
    if (formData.provincia) {
      errors.provincia = errors.provincia || provinciaIT(formData.provincia)
    }
  }
  
  return Object.fromEntries(
    Object.entries(errors).filter(([_, v]) => v !== null)
  )
}

/**
 * Valida form venditore
 */
export const validateVenditore = (formData) => {
  const errors = {}
  
  errors.nome = required(formData.nome) || minLength(formData.nome, 2)
  errors.cognome = required(formData.cognome) || minLength(formData.cognome, 2)
  
  if (formData.telefono) {
    errors.telefono = telefono(formData.telefono)
  }
  
  return Object.fromEntries(
    Object.entries(errors).filter(([_, v]) => v !== null)
  )
}

/**
 * Valida form destinatario
 */
export const validateDestinatario = (formData) => {
  const errors = {}
  
  // Almeno nome o azienda deve essere presente
  if (!formData.nome && !formData.azienda) {
    errors.nome = 'Inserire nome o azienda'
    errors.azienda = 'Inserire nome o azienda'
  }
  
  // Telefono obbligatorio
  errors.telefono = required(formData.telefono) || telefono(formData.telefono)
  
  // Email opzionale ma se presente deve essere valida
  if (formData.email) {
    errors.email = email(formData.email)
  }
  
  return Object.fromEntries(
    Object.entries(errors).filter(([_, v]) => v !== null)
  )
}

/**
 * Valida form prodotto
 */
export const validateProdotto = (formData) => {
  const errors = {}

  // codice: auto-generato backend, non più validato lato frontend
  errors.nome = required(formData.nome) || minLength(formData.nome, 3)
  errors.prezzo_acquisto_euro = required(formData.prezzo_acquisto_euro) || numeroNonNegativo(formData.prezzo_acquisto_euro)
  errors.prezzo_vendita_euro = required(formData.prezzo_vendita_euro) || numeroPositivo(formData.prezzo_vendita_euro)
  
  // Prezzo vendita > prezzo acquisto
  if (formData.prezzo_acquisto_euro && formData.prezzo_vendita_euro) {
    const acquisto = parseFloat(formData.prezzo_acquisto_euro)
    const vendita = parseFloat(formData.prezzo_vendita_euro)
    if (!isNaN(acquisto) && !isNaN(vendita) && vendita <= acquisto) {
      errors.prezzo_vendita_euro = MESSAGES.PREZZO_VENDITA_MAGGIORE
    }
  }
  
  if (formData.peso_grammi) {
    errors.peso_grammi = numeroNonNegativo(formData.peso_grammi)
  }
  
  errors.soglia_minima_giacenza = parseThreshold(formData.soglia_minima_giacenza) === null
    ? 'La soglia deve essere un intero non negativo.'
    : null
  
  return Object.fromEntries(
    Object.entries(errors).filter(([_, v]) => v !== null)
  )
}

/**
 * Valida form box (tipologia)
 */
export const validateBox = (formData) => {
  const errors = {}

  // codice: auto-generato backend, non più validato lato frontend
  errors.nome = required(formData.nome) || minLength(formData.nome, 3)
  errors.configurazione = required(formData.configurazione, 'Seleziona una configurazione')
  
  const prezzoCents = euroToCents(formData.prezzo_vendita_euro)
  errors.prezzo_vendita_euro = prezzoCents === null || prezzoCents <= 0
    ? 'Inserisci un prezzo in euro maggiore di zero, con massimo due decimali (es. 25,50).'
    : null
  
  return Object.fromEntries(
    Object.entries(errors).filter(([_, v]) => v !== null)
  )
}

/**
 * Valida form componente scatola
 */
export const validateComponente = (formData) => {
  const errors = {}

  // codice: auto-generato backend, non più validato lato frontend
  errors.nome = required(formData.nome) || minLength(formData.nome, 3)
  errors.tipo = required(formData.tipo, 'Seleziona un tipo')
  
  if (formData.costo_euro) {
    errors.costo_euro = numeroNonNegativo(formData.costo_euro)
  }
  
  // Dimensioni opzionali ma se presenti devono essere positive
  if (formData.larghezza_cm) {
    errors.larghezza_cm = numeroPositivo(formData.larghezza_cm)
  }
  if (formData.altezza_cm) {
    errors.altezza_cm = numeroPositivo(formData.altezza_cm)
  }
  if (formData.profondita_cm) {
    errors.profondita_cm = numeroPositivo(formData.profondita_cm)
  }
  if (formData.peso_grammi) {
    errors.peso_grammi = numeroNonNegativo(formData.peso_grammi)
  }
  
  return Object.fromEntries(
    Object.entries(errors).filter(([_, v]) => v !== null)
  )
}

/**
 * Valida form configurazione scatola
 */
export const validateConfigurazione = (formData) => {
  const errors = {}

  // codice: auto-generato backend, non più validato lato frontend
  errors.nome = required(formData.nome) || minLength(formData.nome, 3)
  errors.struttura_imballo = required(formData.struttura_imballo, 'Seleziona il tipo di imballo')
  
  // Dimensioni opzionali ma se presenti devono essere positive
  if (formData.larghezza_interna_cm) {
    errors.larghezza_interna_cm = numeroPositivo(formData.larghezza_interna_cm)
  }
  if (formData.altezza_interna_cm) {
    errors.altezza_interna_cm = numeroPositivo(formData.altezza_interna_cm)
  }
  if (formData.profondita_interna_cm) {
    errors.profondita_interna_cm = numeroPositivo(formData.profondita_interna_cm)
  }
  
  return Object.fromEntries(
    Object.entries(errors).filter(([_, v]) => v !== null)
  )
}

/**
 * Valida form collo
 */
export const validateCollo = (formData) => {
  const errors = {}
  
  errors.spedizione = required(formData.spedizione, 'Seleziona una spedizione')
  errors.destinatario = required(formData.destinatario, 'Seleziona un destinatario')
  
  if (formData.peso_kg) {
    errors.peso_kg = numeroPositivo(formData.peso_kg)
  }
  
  // Dimensioni opzionali ma se presenti devono essere positive
  if (formData.lunghezza_cm) {
    errors.lunghezza_cm = numeroPositivo(formData.lunghezza_cm)
  }
  if (formData.larghezza_cm) {
    errors.larghezza_cm = numeroPositivo(formData.larghezza_cm)
  }
  if (formData.altezza_cm) {
    errors.altezza_cm = numeroPositivo(formData.altezza_cm)
  }
  
  if (formData.valore_dichiarato_euro) {
    errors.valore_dichiarato_euro = numeroNonNegativo(formData.valore_dichiarato_euro)
  }
  
  return Object.fromEntries(
    Object.entries(errors).filter(([_, v]) => v !== null)
  )
}

// ============================================
// Helper per controllare se ci sono errori
// ============================================

/**
 * Controlla se un oggetto errori ha almeno un errore
 */
export const hasErrors = (errors) => {
  return Object.keys(errors).length > 0
}

/**
 * Esporta tutto come default
 */
export default {
  PATTERNS,
  MESSAGES,
  required,
  minLength,
  maxLength,
  pattern,
  email,
  capIT,
  provinciaIT,
  codiceFiscale,
  partitaIva,
  codiceSdi,
  telefono,
  codiceAlfanumerico,
  numeroPositivo,
  numeroNonNegativo,
  validateCliente,
  validateIndirizzo,
  validateVenditore,
  validateDestinatario,
  validateProdotto,
  validateBox,
  validateComponente,
  validateConfigurazione,
  validateCollo,
  hasErrors,
}
